import type { AudioEngine } from './AudioEngine.js';

export interface WindSoundOptions {
  /**
   * Master multiplier on the wind volume — for vehicle profiles. 1.0 = sedan
   * default. Cabs / enclosed vehicles ≈ 0.6. Convertibles ≈ 1.4. Motorcycles
   * ≈ 2.5 (open helmet wind, full-body airflow). Applied to both whoosh and
   * rumble equally; switchable at runtime via `setIntensity()`.
   */
  intensity?: number;
}

/**
 * Speed-driven wind / road noise. Independent of throttle — what you hear when
 * the car is moving fast, even if you let off the accelerator.
 *
 * Whoosh is a **stereo pair** of decorrelated sources, each with its own
 * filter chain and statically panned to ±SPREAD. That gives the wind an
 * "around you" feel rather than mono "in your head". Rumble stays mono
 * because frequencies below ~80 Hz can't be localised by the ear anyway.
 *
 *   noiseL → hpfL → lpfL → spreadL(-SPREAD) ─┐
 *                                            ├─→ whooshGain → mute → pan(obstacle) → master
 *   noiseR → hpfR → lpfR → spreadR(+SPREAD) ─┘
 *
 *   rumbleNoise → lpf(120Hz) → rumbleGain → mute                              → master
 *
 * The downstream `whooshPan` is driven by lateralBias from
 * `Vehicle.getNearestSideSource()` — when you pass a wall on the right, the
 * whole stereo image biases right. With the pre-spread already wide, this
 * gives both a sense of constant ambient wind AND a directional Doppler cue.
 *
 * Vehicle profile (cab / sedan / convertible / motorcycle) is selected via
 * `setIntensity(scale)`. The scalar is folded into the gain math in update():
 *   - Whoosh tracks intensity 1:1 (high freqs are heavily attenuated by
 *     cabin insulation, so different vehicles really do sound very different)
 *   - Rumble is damped to 30% of intensity (low freqs pass through cabins,
 *     helmets, and walls — bass through a neighbour's wall, etc.)
 *   - The proximity-driven Doppler boost is NOT scaled at all, so the
 *     "whoosh past wall" effect stays at full strength for every profile.
 */
export class WindSound {
  private ctx: AudioContext;
  // Whoosh — dual-source stereo. The two halves each have their own filter
  // chain so they remain decorrelated; sharing filters would sum-mix them
  // before panning and collapse the stereo image back to mono.
  private whooshNoiseL: AudioBufferSourceNode;
  private whooshNoiseR: AudioBufferSourceNode;
  private whooshHpfL: BiquadFilterNode;
  private whooshHpfR: BiquadFilterNode;
  private whooshLpfL: BiquadFilterNode;
  private whooshLpfR: BiquadFilterNode;
  private whooshSpreadL: StereoPannerNode;
  private whooshSpreadR: StereoPannerNode;
  private whooshGain: GainNode;
  private whooshVolumeGain: GainNode;
  private whooshMuteGain: GainNode;
  private whooshPan: StereoPannerNode;
  private whooshMuted = false;
  private whooshVolume = 1.0;
  private rumbleNoise: AudioBufferSourceNode;
  private rumbleLpf: BiquadFilterNode;
  private rumbleGain: GainNode;
  private rumbleVolumeGain: GainNode;
  private rumbleMuteGain: GainNode;
  private rumbleMuted = false;
  private rumbleVolume = 1.0;
  /**
   * Vehicle-profile wind-volume scalar. Applied to the BASE wind only, not to
   * the Doppler proximity boost — so even a well-isolated cab (low intensity)
   * still gets the full passing-wall whoosh, which is half the fun of driving.
   * Read in update() and folded into the gain target math; setIntensity()
   * just stores the new value, the next frame's setTargetAtTime smooths it.
   */
  private intensity: number;
  private started = false;

  constructor(audio: AudioEngine, noiseBuf: AudioBuffer, opts: WindSoundOptions = {}) {
    this.ctx = audio.ctx;
    this.intensity = Math.max(0, opts.intensity ?? 1.0);

    // ---- Whoosh layer: dual-source stereo for ambient wind spread ----
    // ±0.7 puts the sources just inside fully hard-panned. Wider than typical
    // music stereo (which sits around ±0.5) so the wind feels like it wraps
    // the listener; pulled back from ±1.0 so it doesn't sound disorienting.
    const SPREAD = 0.7;

    this.whooshNoiseL = this.ctx.createBufferSource();
    this.whooshNoiseL.buffer = noiseBuf;
    this.whooshNoiseL.loop = true;

    this.whooshNoiseR = this.ctx.createBufferSource();
    this.whooshNoiseR.buffer = noiseBuf;
    this.whooshNoiseR.loop = true;

    // Highpass (250 Hz, Q=0.5) keeps whoosh out of the bass range; rumble
    // owns those frequencies. One per side — sharing would mix them down
    // before panning and kill the stereo decorrelation.
    this.whooshHpfL = this.ctx.createBiquadFilter();
    this.whooshHpfL.type = 'highpass';
    this.whooshHpfL.frequency.value = 250;
    this.whooshHpfL.Q.value = 0.5;

    this.whooshHpfR = this.ctx.createBiquadFilter();
    this.whooshHpfR.type = 'highpass';
    this.whooshHpfR.frequency.value = 250;
    this.whooshHpfR.Q.value = 0.5;

    // Lowpass cutoff opens with speed — at standstill, near-closed; at speed,
    // wide open so the high-frequency hiss/whoosh comes through. Updated
    // every frame in update() on BOTH filters with the same value.
    this.whooshLpfL = this.ctx.createBiquadFilter();
    this.whooshLpfL.type = 'lowpass';
    this.whooshLpfL.frequency.value = 600;
    this.whooshLpfL.Q.value = 0.7;

    this.whooshLpfR = this.ctx.createBiquadFilter();
    this.whooshLpfR.type = 'lowpass';
    this.whooshLpfR.frequency.value = 600;
    this.whooshLpfR.Q.value = 0.7;

    // Static base-wind pans — sources sit at ±SPREAD so the wind has a wide
    // stereo image even with the obstacle pan at 0. Independent of, and
    // composes with, the downstream obstacle-side pan.
    this.whooshSpreadL = this.ctx.createStereoPanner();
    this.whooshSpreadL.pan.value = -SPREAD;

    this.whooshSpreadR = this.ctx.createStereoPanner();
    this.whooshSpreadR.pan.value = +SPREAD;

    this.whooshGain = this.ctx.createGain();
    this.whooshGain.gain.value = 0.0;

    // User volume gain (Settings → Sounds slider, persisted).
    this.whooshVolumeGain = this.ctx.createGain();
    this.whooshVolumeGain.gain.value = this.whooshVolume;

    // User mute gain — separate node so the speed-driven envelope doesn't fight it.
    this.whooshMuteGain = this.ctx.createGain();
    this.whooshMuteGain.gain.value = 1.0;

    // Obstacle-side pan — animated by lateralBias from
    // Vehicle.getNearestSideSource(). pushes the whole already-wide stereo
    // image toward the wall side. StereoPannerNode is constant-power, cheap.
    this.whooshPan = this.ctx.createStereoPanner();
    this.whooshPan.pan.value = 0;

    // Wire each side through its own filter pair + spread pan, then merge
    // at the shared whooshGain (speed envelope) → mute → obstacle pan → master.
    // No intensity node in the chain — intensity is applied selectively in
    // update() so the Doppler boost stays at full strength regardless of profile.
    this.whooshNoiseL
      .connect(this.whooshHpfL)
      .connect(this.whooshLpfL)
      .connect(this.whooshSpreadL)
      .connect(this.whooshGain);
    this.whooshNoiseR
      .connect(this.whooshHpfR)
      .connect(this.whooshLpfR)
      .connect(this.whooshSpreadR)
      .connect(this.whooshGain);
    this.whooshGain
      .connect(this.whooshVolumeGain)
      .connect(this.whooshMuteGain)
      .connect(this.whooshPan)
      .connect(audio.master);

    // ---- Rumble layer: low-frequency road/turbulence weight ----
    // Separate AudioBufferSourceNode from the same buffer — Web Audio allows
    // multiple sources on a single buffer; each has its own playhead so they
    // desynchronise naturally for a richer composite noise field.
    this.rumbleNoise = this.ctx.createBufferSource();
    this.rumbleNoise.buffer = noiseBuf;
    this.rumbleNoise.loop = true;

    // Lowpass at 120 Hz with a slight peak (Q=1) gives a deep, focused rumble
    // rather than a flat hum. This is the layer that adds "bass at high speed".
    this.rumbleLpf = this.ctx.createBiquadFilter();
    this.rumbleLpf.type = 'lowpass';
    this.rumbleLpf.frequency.value = 120;
    this.rumbleLpf.Q.value = 1.0;

    this.rumbleGain = this.ctx.createGain();
    this.rumbleGain.gain.value = 0.0;

    this.rumbleVolumeGain = this.ctx.createGain();
    this.rumbleVolumeGain.gain.value = this.rumbleVolume;

    this.rumbleMuteGain = this.ctx.createGain();
    this.rumbleMuteGain.gain.value = 1.0;

    this.rumbleNoise
      .connect(this.rumbleLpf)
      .connect(this.rumbleGain)
      .connect(this.rumbleVolumeGain)
      .connect(this.rumbleMuteGain)
      .connect(audio.master);
  }

  setWhooshVolume(v: number): void {
    this.whooshVolume = clamp01(v);
    this.whooshVolumeGain.gain.setTargetAtTime(this.whooshVolume, this.ctx.currentTime, 0.05);
  }

  getWhooshVolume(): number {
    return this.whooshVolume;
  }

  setRumbleVolume(v: number): void {
    this.rumbleVolume = clamp01(v);
    this.rumbleVolumeGain.gain.setTargetAtTime(this.rumbleVolume, this.ctx.currentTime, 0.05);
  }

  getRumbleVolume(): number {
    return this.rumbleVolume;
  }

  /**
   * Set the wind-volume scalar for the current vehicle profile. Default 1.0;
   * higher = louder wind (motorcycles, convertibles), lower = muffled (cab).
   * Only the BASE wind (constant high-speed component) is scaled — the
   * Doppler proximity boost stays at full strength regardless, so even an
   * isolated cab gives the full whoosh-past-wall effect. Smoothing is carried
   * by the existing per-frame setTargetAtTime on whoosh/rumble gain (~100 ms).
   */
  setIntensity(scale: number): void {
    this.intensity = Math.max(0, scale);
  }

  getIntensity(): number {
    return this.intensity;
  }

  setWhooshMuted(m: boolean): void {
    this.whooshMuted = m;
    this.whooshMuteGain.gain.setTargetAtTime(m ? 0 : 1, this.ctx.currentTime, 0.05);
  }

  toggleWhooshMuted(): boolean {
    this.setWhooshMuted(!this.whooshMuted);
    return this.whooshMuted;
  }

  isWhooshMuted(): boolean {
    return this.whooshMuted;
  }

  setRumbleMuted(m: boolean): void {
    this.rumbleMuted = m;
    this.rumbleMuteGain.gain.setTargetAtTime(m ? 0 : 1, this.ctx.currentTime, 0.05);
  }

  toggleRumbleMuted(): boolean {
    this.setRumbleMuted(!this.rumbleMuted);
    return this.rumbleMuted;
  }

  isRumbleMuted(): boolean {
    return this.rumbleMuted;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    // Random offsets into the shared noise buffer so all four whoosh+rumble
    // sources are decorrelated from each other AND from the engine/tire noise.
    // Critical for the stereo whoosh — if L and R started at the same buffer
    // offset, they'd be identical sample-for-sample and panning to ±0.7 would
    // collapse to mono. Random offsets guarantee uncorrelated streams.
    const dur = this.whooshNoiseL.buffer?.duration ?? 0;
    this.whooshNoiseL.start(0, Math.random() * dur);
    this.whooshNoiseR.start(0, Math.random() * dur);
    this.rumbleNoise.start(0, Math.random() * (this.rumbleNoise.buffer?.duration ?? 0));
  }

  /**
   * Call every frame.
   * @param speed             m/s — vehicle linear speed
   * @param sideProximity     0..1 — closeness of nearest sideways source. Drives
   *                          the "near a wall" gain/cutoff boost (the perceptual
   *                          "compression" cheat — not real reflection physics,
   *                          but cheaper than a ConvolverNode).
   * @param approachVelocity  m/s, positive = approaching the source, negative =
   *                          receding. Drives the real Doppler pitch shift via
   *                          playbackRate. The formula is the physics-textbook
   *                          one: ratio = (v_sound + v_approach) / v_sound.
   * @param lateralBias       -1..+1 — sign indicates which side dominates
   *                          (-1 = left, +1 = right). Pans the whoosh layer
   *                          via StereoPannerNode so the Doppler comes from
   *                          the correct ear.
   */
  update(speed: number, sideProximity = 0, approachVelocity = 0, lateralBias = 0): void {
    if (!this.started) return;
    const now = this.ctx.currentTime;
    const TAU = 0.1;
    // Doppler shift needs to track approach velocity tightly — the signal
    // swings full→0→negative in a few hundred ms when passing a wall.
    const TAU_FAST = 0.03;

    const kmh = speed * 3.6;
    const prox = clamp01(sideProximity);

    // Piecewise linear gain curves:
    //   0–60 km/h    silent
    //   60–100       ramps from 0 to (lowEnd) — a hint of wind starts emerging
    //   100–300      ramps from (lowEnd) to (peak) — main usable range
    //   300+         capped at (peak)
    const whooshBase = windCurve(kmh, 0.04, 0.30);
    const rumbleBase = windCurve(kmh, 0.07, 0.40);
    // Intensity scales the BASE wind only — the proximity-driven boost (the
    // "whoosh past a wall" Doppler companion) is added at full strength on
    // top, so a heavily-isolated vehicle still gets the full obstacle effect.
    //
    // Whoosh tracks intensity 1:1. Rumble is damped to 30% of the intensity
    // effect because low frequencies pass through structures (cabin walls,
    // helmets) far better than high frequencies — that's basic acoustics, the
    // same reason you hear bass through a wall but not vocals. So a cab
    // muffles the whoosh heavily but still rumbles; a motorcycle adds lots
    // of whoosh and a bit more rumble.
    const RUMBLE_INTENSITY_DAMP = 0.3;
    const rumbleIntensity = 1 + (this.intensity - 1) * RUMBLE_INTENSITY_DAMP;
    const whooshTarget = whooshBase * this.intensity + whooshBase * prox * 1.2;
    const rumbleTarget = rumbleBase * rumbleIntensity + rumbleBase * prox * 0.5;
    this.whooshGain.gain.setTargetAtTime(whooshTarget, now, TAU);
    this.rumbleGain.gain.setTargetAtTime(rumbleTarget, now, TAU);

    // Whoosh LPF cutoff opens with speed across the same usable band (60→300).
    // Proximity pushes it further up — tight gaps sound brighter/sharper.
    // Both L and R filters track the same cutoff so the timbre stays
    // symmetric; only the underlying sample data differs between the two.
    const cutoffT = clamp01((kmh - 60) / 240);
    const cutoff = 600 + cutoffT * 3000 + prox * 1500;
    this.whooshLpfL.frequency.setTargetAtTime(cutoff, now, TAU);
    this.whooshLpfR.frequency.setTargetAtTime(cutoff, now, TAU);

    // Real Doppler shift via playbackRate. Applied to BOTH whoosh sources —
    // Doppler is positional pitch shift, not a stereo effect, so both ears
    // hear the same pitch swoop. The L/R panning carries the directional cue.
    //   f' / f = (v_sound + v_approach) / v_sound = 1 + v_approach / v_sound
    // At 100 km/h head-on, that's ~+8%; at 300 km/h ~+24%. As the car passes
    // the wall, approachVelocity naturally swings positive → 0 (tangent) →
    // negative (receding), producing the "ambulance siren" descending glide.
    // Rumble stays solid — only whoosh Doppler-shifts so the bass doesn't wobble.
    const SPEED_OF_SOUND = 343; // m/s in air at ~20°C
    const dopplerRatio = 1 + approachVelocity / SPEED_OF_SOUND;
    this.whooshNoiseL.playbackRate.setTargetAtTime(dopplerRatio, now, TAU_FAST);
    this.whooshNoiseR.playbackRate.setTargetAtTime(dopplerRatio, now, TAU_FAST);

    // Stereo pan: lateralBias is already in [-1, +1] from getNearestSideSource,
    // which is exactly the range StereoPannerNode.pan expects. Fast smoothing
    // so the pan tracks the wall-passing peak without lag.
    const pan = lateralBias < -1 ? -1 : lateralBias > 1 ? 1 : lateralBias;
    this.whooshPan.pan.setTargetAtTime(pan, now, TAU_FAST);
  }
}

/**
 * Three-segment piecewise linear ramp shared by both wind layers.
 * @param kmh    current speed
 * @param lowEnd value at 100 km/h (end of the introductory ramp)
 * @param peak   value at 300 km/h and above (cap)
 */
function windCurve(kmh: number, lowEnd: number, peak: number): number {
  if (kmh <= 60) return 0;
  if (kmh <= 100) return lowEnd * ((kmh - 60) / 40);
  if (kmh <= 300) return lowEnd + (peak - lowEnd) * ((kmh - 100) / 200);
  return peak;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
